import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { CalendarDaysIcon, CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, GripVerticalIcon, PackagePlusIcon, SearchIcon } from 'lucide-react';
import { Button } from './Button';
import { Card } from './Card';
import { Modal } from './Modal';
import { Select } from './Select';
import { TextInput } from './TextInput';
type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri';
type OrderStatus = 'Geplant' | 'Entwurf';
type PlannerDay = {
  key: DayKey;
  shortLabel: string;
  date: string;
  dateLabel: string;
};
type PlannerRecipe = {
  id: string;
  number: string;
  name: string;
  kilogramsPerLot: number;
};
type PlannedOrder = {
  id: string;
  recipeId: string;
  day: DayKey;
  lots: number;
  status: OrderStatus;
  scaledLots: number;
  source?: 'sap';
};
type FormValues = {
  orderNumber: string;
  recipeId: string;
  day: DayKey;
  lots: string;
};
type FormErrors = Partial<Record<keyof FormValues, string>>;
const weekDays: PlannerDay[] = [{
  key: 'mon',
  shortLabel: 'Mo',
  date: '13',
  dateLabel: 'Montag, 13. Juli'
}, {
  key: 'tue',
  shortLabel: 'Di',
  date: '14',
  dateLabel: 'Dienstag, 14. Juli'
}, {
  key: 'wed',
  shortLabel: 'Mi',
  date: '15',
  dateLabel: 'Mittwoch, 15. Juli'
}, {
  key: 'thu',
  shortLabel: 'Do',
  date: '16',
  dateLabel: 'Donnerstag, 16. Juli'
}, {
  key: 'fri',
  shortLabel: 'Fr',
  date: '17',
  dateLabel: 'Freitag, 17. Juli'
}];
const recipes: PlannerRecipe[] = [{
  id: 'rz-1042',
  number: 'RZ-1042',
  name: 'Schoko-Crunch',
  kilogramsPerLot: 70
}, {
  id: 'rz-1043',
  number: 'RZ-1043',
  name: 'Frucht-Müsli',
  kilogramsPerLot: 54
}, {
  id: 'rz-1044',
  number: 'RZ-1044',
  name: 'Vanille-Knusper',
  kilogramsPerLot: 48
}, {
  id: 'rz-1045',
  number: 'RZ-1045',
  name: 'Nuss-Mix Premium',
  kilogramsPerLot: 62
}];
const initialOrders: PlannedOrder[] = [{
  id: 'AUF-240713-018',
  recipeId: 'rz-1043',
  day: 'mon',
  lots: 8,
  status: 'Geplant',
  scaledLots: 8
}, {
  id: 'AUF-240713-021',
  recipeId: 'rz-1042',
  day: 'mon',
  lots: 4,
  status: 'Entwurf',
  scaledLots: 2
}, {
  id: 'AUF-240714-022',
  recipeId: 'rz-1045',
  day: 'tue',
  lots: 5,
  status: 'Geplant',
  scaledLots: 5
}, {
  id: 'AUF-240714-025',
  recipeId: 'rz-1044',
  day: 'tue',
  lots: 7,
  status: 'Geplant',
  scaledLots: 4
}, {
  id: 'AUF-240715-031',
  recipeId: 'rz-1042',
  day: 'wed',
  lots: 12,
  status: 'Geplant',
  scaledLots: 12
}, {
  id: 'AUF-240715-028',
  recipeId: 'rz-1043',
  day: 'wed',
  lots: 8,
  status: 'Entwurf',
  scaledLots: 4
}, {
  id: 'AUF-240715-036',
  recipeId: 'rz-1045',
  day: 'wed',
  lots: 6,
  status: 'Geplant',
  scaledLots: 6
}, {
  id: 'AUF-240715-039',
  recipeId: 'rz-1044',
  day: 'wed',
  lots: 5,
  status: 'Geplant',
  scaledLots: 5
}, {
  id: 'AUF-240716-041',
  recipeId: 'rz-1045',
  day: 'thu',
  lots: 6,
  status: 'Geplant',
  scaledLots: 6
}, {
  id: 'AUF-240716-044',
  recipeId: 'rz-1044',
  day: 'thu',
  lots: 4,
  status: 'Entwurf',
  scaledLots: 1
}, {
  id: 'AUF-240717-052',
  recipeId: 'rz-1043',
  day: 'fri',
  lots: 9,
  status: 'Geplant',
  scaledLots: 9
}];
const emptyForm: FormValues = {
  orderNumber: '',
  recipeId: '',
  day: 'wed',
  lots: ''
};
const sapOrderTemplates: Omit<PlannedOrder, 'day'>[] = [{
  id: 'SAP-240715-101',
  recipeId: 'rz-1042',
  lots: 6,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-102',
  recipeId: 'rz-1043',
  lots: 4,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-103',
  recipeId: 'rz-1045',
  lots: 8,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-104',
  recipeId: 'rz-1044',
  lots: 5,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-105',
  recipeId: 'rz-1042',
  lots: 3,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-106',
  recipeId: 'rz-1043',
  lots: 7,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-107',
  recipeId: 'rz-1045',
  lots: 4,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-108',
  recipeId: 'rz-1044',
  lots: 6,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-109',
  recipeId: 'rz-1042',
  lots: 5,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}, {
  id: 'SAP-240715-110',
  recipeId: 'rz-1043',
  lots: 3,
  status: 'Geplant',
  scaledLots: 0,
  source: 'sap'
}];
export function ProductionPlanner() {
  const [selectedDay, setSelectedDay] = useState<DayKey>('wed');
  const [orders, setOrders] = useState<PlannedOrder[]>(initialOrders);
  const [viewedSapDays, setViewedSapDays] = useState<Set<DayKey>>(new Set());
  const [draggedOrderId, setDraggedOrderId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{
    targetOrderId: string;
    position: 'before' | 'after';
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isSapImportOpen, setIsSapImportOpen] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>(emptyForm);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [announcement, setAnnouncement] = useState('');
  const focusedDay = getDay(selectedDay);
  const focusedOrders = orders.filter(order => order.day === selectedDay);
  const focusedTotals = getTotals(focusedOrders);
  const selectedRecipe = recipes.find(recipe => recipe.id === formValues.recipeId);
  const formLots = Number(formValues.lots);
  const formTotalKg = selectedRecipe && Number.isFinite(formLots) && formLots > 0 ? selectedRecipe.kilogramsPerLot * formLots : 0;
  const adjacentDays = useMemo(() => {
    const selectedIndex = weekDays.findIndex(day => day.key === selectedDay);
    return [weekDays[selectedIndex - 1] ?? null, weekDays[selectedIndex + 1] ?? null];
  }, [selectedDay]);
  useEffect(() => {
    const loadingTimeoutId = window.setTimeout(() => setIsLoading(false), 1200);
    return () => window.clearTimeout(loadingTimeoutId);
  }, []);
  function getOrdersForDay(day: DayKey) {
    return orders.filter(order => order.day === day);
  }
  function selectPlanningDay(day: DayKey) {
    setSelectedDay(day);
    setViewedSapDays(current => new Set(current).add(day));
  }
  function openCreateOrder() {
    setFormValues({
      ...emptyForm,
      day: selectedDay
    });
    setFormErrors({});
    setIsOrderModalOpen(true);
  }
  function updateFormValue<Key extends keyof FormValues>(key: Key, value: FormValues[Key]) {
    setFormValues(current => ({
      ...current,
      [key]: value
    }));
    setFormErrors(current => ({
      ...current,
      [key]: undefined
    }));
  }
  function createOrder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedOrderNumber = formValues.orderNumber.trim().toUpperCase();
    const lots = Number(formValues.lots);
    const nextErrors: FormErrors = {};
    if (!normalizedOrderNumber) {
      nextErrors.orderNumber = 'Bitte geben Sie eine Auftragsnummer ein.';
    } else if (orders.some(order => order.id === normalizedOrderNumber)) {
      nextErrors.orderNumber = 'Diese Auftragsnummer ist bereits eingeplant.';
    }
    if (!formValues.recipeId) {
      nextErrors.recipeId = 'Bitte wählen Sie ein Rezept.';
    }
    if (!Number.isInteger(lots) || lots < 1) {
      nextErrors.lots = 'Geben Sie mindestens 1 ganzes Lot ein.';
    }
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setOrders(current => [...current, {
      id: normalizedOrderNumber,
      recipeId: formValues.recipeId,
      day: formValues.day,
      lots,
      status: 'Entwurf',
      scaledLots: 0
    }]);
    setSelectedDay(formValues.day);
    setIsOrderModalOpen(false);
    setAnnouncement(`${normalizedOrderNumber} wurde für ${getDay(formValues.day).dateLabel} eingeplant.`);
  }
  function startDrag(event: React.DragEvent<HTMLElement>, orderId: string) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', orderId);
    setDraggedOrderId(orderId);
  }
  function finishDrag() {
    setDraggedOrderId(null);
    setDropIndicator(null);
  }
  function allowDrop(event: React.DragEvent<HTMLElement>) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }
  function getDropPosition(event: React.DragEvent<HTMLElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    return event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after';
  }
  function updateFocusedDropIndicator(event: React.DragEvent<HTMLElement>, targetOrderId: string) {
    allowDrop(event);
    event.stopPropagation();
    if (!draggedOrderId || draggedOrderId === targetOrderId) {
      setDropIndicator(null);
      return;
    }
    const nextIndicator = {
      targetOrderId,
      position: getDropPosition(event)
    } as const;
    setDropIndicator(current => current?.targetOrderId === nextIndicator.targetOrderId && current.position === nextIndicator.position ? current : nextIndicator);
  }
  function clearFocusedDropIndicator(event?: React.DragEvent<HTMLElement>) {
    if (event?.relatedTarget && event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }
    setDropIndicator(null);
  }
  function handleFocusedListDragOver(event: React.DragEvent<HTMLElement>) {
    allowDrop(event);
    if (event.target === event.currentTarget && draggedOrderId && focusedOrders.length > 0) {
      setDropIndicator({
        targetOrderId: focusedOrders[focusedOrders.length - 1].id,
        position: 'after'
      });
    }
  }
  function moveOrderToDay(day: DayKey, targetOrderId?: string, position: 'before' | 'after' = 'before') {
    if (!draggedOrderId) {
      return;
    }
    setOrders(currentOrders => {
      const movingOrder = currentOrders.find(order => order.id === draggedOrderId);
      if (!movingOrder) {
        return currentOrders;
      }
      const remainingOrders = currentOrders.filter(order => order.id !== draggedOrderId);
      const destinationOrders = remainingOrders.filter(order => order.day === day);
      const targetIndex = targetOrderId ? destinationOrders.findIndex(order => order.id === targetOrderId) : -1;
      const insertionIndex = targetIndex === -1 ? destinationOrders.length : targetIndex + (position === 'after' ? 1 : 0);
      const reorderedDestination = [...destinationOrders];
      reorderedDestination.splice(insertionIndex, 0, {
        ...movingOrder,
        day
      });
      return [...remainingOrders.filter(order => order.day !== day), ...reorderedDestination];
    });
    const dayLabel = getDay(day).dateLabel;
    setAnnouncement(`${draggedOrderId} wurde auf ${dayLabel} verschoben.`);
    setDraggedOrderId(null);
    setDropIndicator(null);
  }
  function handleOrderDrop(event: React.DragEvent<HTMLElement>, targetOrderId: string) {
    event.preventDefault();
    event.stopPropagation();
    const targetOrder = orders.find(order => order.id === targetOrderId);
    if (targetOrder) {
      moveOrderToDay(targetOrder.day, targetOrderId, getDropPosition(event));
    }
  }
  function confirmSapImport() {
    const existingOrderIds = new Set(orders.map(order => order.id));
    const importedOrders = sapOrderTemplates.filter(order => !existingOrderIds.has(order.id)).map((order, index) => ({
      ...order,
      day: weekDays[index % weekDays.length].key
    }));
    setOrders(current => [...current, ...importedOrders]);
    setViewedSapDays(new Set());
    setIsSapImportOpen(false);
    setAnnouncement(importedOrders.length > 0 ? `${importedOrders.length} SAP-Aufträge wurden auf die Planungstage dieser Woche verteilt.` : 'Die SAP-Aufträge wurden bereits dem Produktionsplan hinzugefügt.');
  }
  return <section className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-518">
      <div className="border-b border-border bg-surface" data-id="element-519">
        <header className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-10" data-id="element-520">
          <div className="flex items-center gap-3" data-id="element-521">
            <h1 className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-522">
              Produktionsplan
            </h1>
            <button type="button" onClick={() => setIsSapImportOpen(true)} aria-label="Aufträge aus SAP übernehmen" title="Aufträge aus SAP übernehmen" className="flex h-16 items-center rounded-md px-2 text-text-secondary transition-colors hover:bg-brand-yellow-light hover:text-text-primary focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-523">
              <img src="https://cdn.magicpatterns.com/uploads/sMZooMAHf11pkCTQ8baZc4/sap.svg" alt="" aria-hidden="true" className="h-[60px] w-auto object-contain" data-id="element-524" />
            </button>
          </div>
          <div className="flex items-center gap-2 self-start rounded-lg border border-border bg-surface-subtle p-1.5 sm:self-auto" data-id="element-525">
            <button type="button" className="flex h-9 w-9 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-surface hover:text-text-primary focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" aria-label="Vorherige Woche" data-id="element-526">
              <ChevronLeftIcon size={18} aria-hidden="true" data-id="element-527" />
            </button>
            <span className="rounded-md bg-brand-yellow-light px-3 py-1.5 text-sm font-semibold text-text-primary" data-id="element-528">
              KW 29
            </span>
            <span className="hidden px-1 text-sm text-text-secondary md:inline" data-id="element-529">
              13.–17. Juli 2026
            </span>
            <button type="button" className="flex h-9 w-9 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-surface hover:text-text-primary focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" aria-label="Nächste Woche" data-id="element-530">
              <ChevronRightIcon size={18} aria-hidden="true" data-id="element-531" />
            </button>
          </div>
        </header>

        <nav className="grid grid-cols-5 gap-1.5 overflow-x-auto border-t border-border px-3 pt-3 sm:gap-2 sm:px-8 lg:px-10" aria-label="Planungstag auswählen" data-id="element-532">
          {weekDays.map(day => {
          const dayOrders = getOrdersForDay(day.key);
          const totals = getTotals(dayOrders);
          const hasUnreadSapOrders = dayOrders.some(order => order.source === 'sap') && !viewedSapDays.has(day.key);
          const isSelected = day.key === selectedDay;
          return <button key={day.key} type="button" onClick={() => selectPlanningDay(day.key)} onDragOver={allowDrop} onDrop={event => {
            event.preventDefault();
            moveOrderToDay(day.key);
          }} aria-current={isSelected ? 'date' : undefined} className={`min-w-[104px] rounded-t-lg border px-2 py-3 text-center transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green sm:px-4 ${isSelected ? 'border-border border-b-brand-green bg-surface shadow-[inset_0_-3px_0_0_theme(colors.brand.green)]' : 'border-border bg-surface-subtle hover:bg-brand-yellow-light'}`} data-id="element-533">
                <span className="flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-534">
                  {day.shortLabel}
                  {hasUnreadSapOrders ? <span className="h-2 w-2 rounded-full bg-blue-500" aria-label="Neue SAP-Aufträge importiert" title="Neue SAP-Aufträge importiert" data-id="element-535" /> : null}
                </span>
                <span className="mt-1 block font-heading text-xl leading-none text-text-primary sm:text-2xl" data-id="element-536">
                  {day.date}
                </span>
                <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${isSelected ? 'bg-brand-green text-text-primary' : 'border border-border bg-surface text-text-secondary'}`} data-id="element-537">
                  {dayOrders.length}{' '}
                  {dayOrders.length === 1 ? 'Auftrag' : 'Aufträge'}
                </span>
                <span className="sr-only" data-id="element-538">
                  {formatKg(totals.kilograms)} geplant
                </span>
              </button>;
        })}
        </nav>
      </div>

      <div className="mx-auto w-full max-w-[1680px] px-4 py-5 sm:px-6 lg:px-8" data-id="element-539">
        {isLoading ? <ProductionPlannerSkeleton data-id="element-540" /> : <div className="grid gap-4 xl:grid-cols-[172px_minmax(0,1fr)_172px]" data-id="element-541">
            <CompactDay day={adjacentDays[0]} orders={adjacentDays[0] ? getOrdersForDay(adjacentDays[0].key) : []} draggedOrderId={draggedOrderId} onSelectDay={selectPlanningDay} onDragOver={allowDrop} onDrop={moveOrderToDay} data-id="element-542" />

            <main className="min-w-0" data-id="element-543">
              <Card padding="none" elevation="sm" className="overflow-hidden border border-border bg-surface" data-id="element-544">
                <div className="flex flex-col gap-5 border-b border-border px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-545">
                  <div data-id="element-546">
                    <p className="text-sm font-medium text-text-secondary" data-id="element-547">
                      Tagesfokus
                    </p>
                    <h2 className="mt-1 font-heading text-2xl leading-tight sm:text-3xl" data-id="element-548">
                      {focusedDay.dateLabel}
                    </h2>
                    <p className="mt-2 text-sm text-text-secondary" data-id="element-549">
                      {focusedOrders.length} Aufträge geplant ·{' '}
                      {formatKg(focusedTotals.kilograms)} gesamt
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2.5" data-id="element-550">
                    <Statistic value={String(focusedOrders.length)} label="Aufträge" data-id="element-551" />
                    <Statistic value={formatKg(focusedTotals.kilograms)} label="gesamt" data-id="element-552" />
                    <Statistic value={String(focusedTotals.lots)} label="Lots" data-id="element-553" />
                    <Button size="sm" onClick={openCreateOrder} leadingIcon={<PackagePlusIcon size={16} aria-hidden="true" data-id="element-555" />} data-id="element-554">
                      Auftrag anlegen
                    </Button>
                  </div>
                </div>

                <div className="bg-surface-subtle px-5 py-3 text-xs text-text-secondary sm:px-6" data-id="element-556">
                  <span className="inline-flex items-center gap-1.5" data-id="element-557">
                    <GripVerticalIcon size={14} aria-hidden="true" data-id="element-558" />
                    Ziehen Sie Aufträge, um die Reihenfolge oder den Planungstag
                    zu ändern.
                  </span>
                </div>

                <div className="min-h-[470px] space-y-3 p-4 sm:p-6" onDragOver={handleFocusedListDragOver} onDragLeave={clearFocusedDropIndicator} onDrop={event => {
              event.preventDefault();
              moveOrderToDay(selectedDay);
            }} data-id="element-559">
                  <LayoutGroup data-id="element-560">
                    <AnimatePresence initial={false} data-id="element-561">
                      {focusedOrders.length > 0 ? focusedOrders.map((order, index) => <Fragment key={order.id} data-id="element-562">
                            {dropIndicator?.targetOrderId === order.id && dropIndicator.position === 'before' ? <InsertionIndicator data-id="element-563" /> : null}
                            <FocusedOrderCard order={order} recipe={getRecipe(order.recipeId)} priority={index + 1} isDragging={draggedOrderId === order.id} onDragStart={startDrag} onDragEnd={finishDrag} onDragOver={updateFocusedDropIndicator} onDrop={handleOrderDrop} data-id="element-564" />
                            {dropIndicator?.targetOrderId === order.id && dropIndicator.position === 'after' ? <InsertionIndicator data-id="element-565" /> : null}
                          </Fragment>) : <motion.div initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="flex min-h-[300px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-surface-subtle px-6 text-center" data-id="element-566">
                          <CalendarDaysIcon size={28} className="text-text-secondary" aria-hidden="true" data-id="element-567" />
                          <h3 className="mt-4 font-heading text-lg" data-id="element-568">
                            Noch keine Aufträge geplant
                          </h3>
                          <p className="mt-1 max-w-sm text-sm leading-6 text-text-secondary" data-id="element-569">
                            Legen Sie den ersten Auftrag an oder ziehen Sie
                            einen Auftrag aus einem anderen Tag hierher.
                          </p>
                          <Button size="sm" className="mt-5" onClick={openCreateOrder} data-id="element-570">
                            Auftrag anlegen
                          </Button>
                        </motion.div>}
                    </AnimatePresence>
                  </LayoutGroup>
                </div>
              </Card>
            </main>

            <CompactDay day={adjacentDays[1]} orders={adjacentDays[1] ? getOrdersForDay(adjacentDays[1].key) : []} draggedOrderId={draggedOrderId} onSelectDay={selectPlanningDay} onDragOver={allowDrop} onDrop={moveOrderToDay} align="right" data-id="element-571" />
          </div>}
      </div>

      <p className="sr-only" aria-live="polite" data-id="element-572">
        {announcement}
      </p>

      <Modal open={isSapImportOpen} onOpenChange={setIsSapImportOpen} title="SAP-Aufträge übernehmen" description="Es wurden 30 Aufträge in SAP gefunden. Sollen diese dem Produktionsplan hinzugefügt werden?" size="sm" footer={<>
            <Button type="button" variant="secondary" onClick={() => setIsSapImportOpen(false)} data-id="element-574">
              Abbrechen
            </Button>
            <Button type="button" onClick={confirmSapImport} data-id="element-575">
              Aufträge hinzufügen
            </Button>
          </>} data-id="element-573">
        <div className="rounded-lg border border-border bg-surface-subtle p-4" data-id="element-576">
          <p className="font-heading text-2xl text-text-primary" data-id="element-577">30 Aufträge</p>
          <p className="mt-1 text-sm leading-6 text-text-secondary" data-id="element-578">
            Die gefundenen Aufträge werden zur weiteren Einplanung in den
            Produktionsplan übernommen.
          </p>
        </div>
      </Modal>

      <Modal open={isOrderModalOpen} onOpenChange={setIsOrderModalOpen} title="Auftrag einplanen" description="Jedes Lot entspricht einer vollständigen Rezeptur. Die Gesamtmenge wird automatisch skaliert." size="lg" footer={<>
            <Button type="button" variant="secondary" onClick={() => setIsOrderModalOpen(false)} data-id="element-580">
              Abbrechen
            </Button>
            <Button type="submit" form="create-planned-order" data-id="element-581">
              Auftrag einplanen
            </Button>
          </>} data-id="element-579">
        <form id="create-planned-order" noValidate onSubmit={createOrder} data-id="element-582">
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-583">
            <TextInput id="planner-order-number" name="orderNumber" label="Auftragsnummer" placeholder="z. B. AUF-240715-045" value={formValues.orderNumber} onChange={event => updateFormValue('orderNumber', event.target.value)} error={formErrors.orderNumber} required data-id="element-584" />
            <RecipeCombobox id="planner-recipe" recipes={recipes} value={formValues.recipeId} error={formErrors.recipeId} onChange={recipeId => updateFormValue('recipeId', recipeId)} data-id="element-585" />
            <Select id="planner-day" name="day" label="Planungstag" options={weekDays.map(day => ({
            value: day.key,
            label: day.dateLabel
          }))} value={formValues.day} onChange={event => updateFormValue('day', event.target.value as DayKey)} data-id="element-586" />
            <TextInput id="planner-lots" name="lots" type="number" min="1" step="1" inputMode="numeric" label="Anzahl Lots" hint="Ein Lot ist immer eine vollständige Rezeptur." placeholder="z. B. 8" value={formValues.lots} onChange={event => updateFormValue('lots', event.target.value)} error={formErrors.lots} required data-id="element-587" />
          </div>

          <div className="mt-5 grid gap-3 rounded-lg border border-border bg-surface-subtle p-4 sm:grid-cols-3" data-id="element-588">
            <SummaryMetric label="Pro Lot" value={selectedRecipe ? formatKg(selectedRecipe.kilogramsPerLot) : '—'} data-id="element-589" />
            <SummaryMetric label="Lots" value={formValues.lots && Number(formValues.lots) > 0 ? formValues.lots : '—'} data-id="element-590" />
            <SummaryMetric label="Gesamtmenge" value={formTotalKg > 0 ? formatKg(formTotalKg) : '—'} isEmphasized data-id="element-591" />
          </div>
        </form>
      </Modal>
    </section>;
}
type RecipeComboboxProps = {
  id: string;
  recipes: PlannerRecipe[];
  value: string;
  error?: string;
  onChange: (recipeId: string) => void;
};
function RecipeCombobox({
  id,
  recipes,
  value,
  error,
  onChange
}: RecipeComboboxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const selectedRecipe = recipes.find(recipe => recipe.id === value);
  const normalizedQuery = query.trim().toLocaleLowerCase('de-DE');
  const filteredRecipes = recipes.filter(recipe => `${recipe.number} ${recipe.name}`.toLocaleLowerCase('de-DE').includes(normalizedQuery));
  const listboxId = `${id}-listbox`;
  const errorId = `${id}-error`;
  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);
  useEffect(() => {
    setActiveIndex(0);
  }, [query, isOpen]);
  function openSearch() {
    setIsOpen(true);
    setQuery('');
  }
  function selectRecipe(recipe: PlannerRecipe) {
    onChange(recipe.id);
    setQuery('');
    setIsOpen(false);
  }
  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(current => isOpen ? Math.min(current + 1, Math.max(filteredRecipes.length - 1, 0)) : 0);
      setIsOpen(true);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(current => isOpen ? Math.max(current - 1, 0) : Math.max(filteredRecipes.length - 1, 0));
      setIsOpen(true);
      return;
    }
    if (event.key === 'Enter' && isOpen && filteredRecipes[activeIndex]) {
      event.preventDefault();
      selectRecipe(filteredRecipes[activeIndex]);
      return;
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  }
  const displayValue = isOpen ? query : selectedRecipe ? `${selectedRecipe.number} · ${selectedRecipe.name}` : '';
  return <div ref={containerRef} className="relative" data-id="element-592">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-text-primary" data-id="element-593">
        Rezept <span aria-hidden="true" data-id="element-594">*</span>
      </label>
      <div className="relative" data-id="element-595">
        <SearchIcon size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" aria-hidden="true" data-id="element-596" />
        <input id={id} name="recipe" type="text" role="combobox" autoComplete="off" aria-autocomplete="list" aria-expanded={isOpen} aria-controls={listboxId} aria-activedescendant={isOpen && filteredRecipes[activeIndex] ? `${id}-option-${filteredRecipes[activeIndex].id}` : undefined} aria-describedby={error ? errorId : undefined} aria-invalid={Boolean(error)} placeholder="Rezept suchen" value={displayValue} onFocus={openSearch} onClick={openSearch} onChange={event => {
        setQuery(event.target.value);
        setIsOpen(true);
      }} onKeyDown={handleKeyDown} className={`h-11 w-full rounded-md border bg-surface py-2 pl-10 pr-10 text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary focus:border-brand-green focus:ring-4 focus:ring-brand-green/20 ${error ? 'border-error' : 'border-border'}`} data-id="element-597" />
        <ChevronDownIcon size={17} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary" aria-hidden="true" data-id="element-598" />
      </div>

      {isOpen ? <div id={listboxId} role="listbox" aria-label="Passende Rezepte" className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-surface p-1 shadow-md" data-id="element-599">
          {filteredRecipes.length > 0 ? filteredRecipes.map((recipe, index) => {
        const isSelected = recipe.id === value;
        const isActive = index === activeIndex;
        return <button key={recipe.id} id={`${id}-option-${recipe.id}`} type="button" role="option" aria-selected={isSelected} onMouseDown={event => event.preventDefault()} onClick={() => selectRecipe(recipe)} className={`flex w-full items-center justify-between gap-3 rounded-sm px-3 py-2.5 text-left transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-green ${isActive ? 'bg-brand-yellow-light' : 'hover:bg-surface-subtle'}`} data-id="element-600">
                  <span className="min-w-0" data-id="element-601">
                    <span className="block font-heading text-sm text-text-primary" data-id="element-602">
                      {recipe.number}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-text-secondary" data-id="element-603">
                      {recipe.name}
                    </span>
                  </span>
                  {isSelected ? <CheckIcon size={17} className="shrink-0 text-brand-green-dark" aria-hidden="true" data-id="element-604" /> : null}
                </button>;
      }) : <p className="px-3 py-5 text-center text-sm text-text-secondary" data-id="element-605">
              Kein passendes Rezept gefunden.
            </p>}
        </div> : null}

      {error ? <p id={errorId} className="mt-1.5 text-sm text-error" role="alert" data-id="element-606">
          {error}
        </p> : null}
    </div>;
}
type CompactDayProps = {
  day: PlannerDay | null;
  orders: PlannedOrder[];
  draggedOrderId: string | null;
  align?: 'right';
  onSelectDay: (day: DayKey) => void;
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  onDrop: (day: DayKey) => void;
};
function CompactDay({
  day,
  orders,
  draggedOrderId,
  align,
  onSelectDay,
  onDragOver,
  onDrop
}: CompactDayProps) {
  if (!day) {
    return <div className="hidden xl:block" aria-hidden="true" data-id="element-607" />;
  }
  const totals = getTotals(orders);
  return <aside className={`hidden min-h-[520px] border border-border bg-surface-subtle p-3 xl:block ${align === 'right' ? 'order-last' : ''}`} onDragOver={onDragOver} onDrop={event => {
    event.preventDefault();
    onDrop(day.key);
  }} aria-label={`Zusammenfassung ${day.dateLabel}`} data-id="element-608">
      <button type="button" onClick={() => onSelectDay(day.key)} className="w-full rounded-md px-1 py-1 text-left transition-colors hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-609">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-610">
          {day.shortLabel} · {day.date}. Juli
        </span>
        <span className="mt-1 block font-heading text-lg" data-id="element-611">
          {formatKg(totals.kilograms)}
        </span>
        <span className="text-xs text-text-secondary" data-id="element-612">
          {orders.length} {orders.length === 1 ? 'Auftrag' : 'Aufträge'}
        </span>
      </button>

      <div className="mt-4 space-y-2" data-id="element-613">
        {orders.slice(0, 3).map(order => {
        const recipe = getRecipe(order.recipeId);
        const progress = Math.round(order.scaledLots / order.lots * 100);
        return <article key={order.id} className={`rounded-lg border bg-surface p-3 transition-opacity ${draggedOrderId === order.id ? 'opacity-40' : 'opacity-100'}`} data-id="element-614">
              <div className="flex items-center gap-1.5" data-id="element-615">
                <p className="min-w-0 flex-1 truncate font-heading text-xs" data-id="element-616">
                  {order.id}
                </p>
                {order.source === 'sap' ? <SapMark compact data-id="element-617" /> : null}
              </div>
              <p className="mt-1 truncate text-xs text-text-secondary" data-id="element-618">
                {recipe.name}
              </p>
              <p className="mt-2 text-xs text-text-secondary" data-id="element-619">
                {order.lots} Lots ·{' '}
                {formatKg(recipe.kilogramsPerLot * order.lots)}
              </p>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-border" data-id="element-620">
                <div className="h-full rounded-full bg-brand-green" style={{
              width: `${progress}%`
            }} data-id="element-621" />
              </div>
            </article>;
      })}
        {orders.length > 3 ? <p className="px-1 pt-1 text-xs text-text-secondary" data-id="element-622">
            + {orders.length - 3} weitere Aufträge
          </p> : null}
        {orders.length === 0 ? <div className="rounded-lg border border-dashed border-border bg-surface px-3 py-7 text-center text-xs leading-5 text-text-secondary" data-id="element-623">
            Auftrag hier ablegen
          </div> : null}
      </div>
    </aside>;
}
type FocusedOrderCardProps = {
  order: PlannedOrder;
  recipe: PlannerRecipe;
  priority: number;
  isDragging: boolean;
  onDragStart: (event: React.DragEvent<HTMLElement>, orderId: string) => void;
  onDragEnd: () => void;
  onDragOver: (event: React.DragEvent<HTMLElement>, targetOrderId: string) => void;
  onDrop: (event: React.DragEvent<HTMLElement>, targetOrderId: string) => void;
};
function FocusedOrderCard({
  order,
  recipe,
  priority,
  isDragging,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}: FocusedOrderCardProps) {
  const totalKg = recipe.kilogramsPerLot * order.lots;
  return <motion.article layout draggable onDragStart={event => onDragStart(event, order.id)} onDragEnd={onDragEnd} onDragOver={event => onDragOver(event, order.id)} onDrop={event => onDrop(event, order.id)} initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: isDragging ? 0.48 : 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -8
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} className={`group cursor-grab overflow-hidden rounded-xl border border-border bg-surface shadow-sm transition-shadow hover:border-text-secondary active:cursor-grabbing ${isDragging ? 'shadow-md' : ''}`} aria-label={`Priorität ${priority}: ${order.id}, ${recipe.name}`} data-id="element-624">
      <div className="flex min-h-[112px]" data-id="element-625">
        <div className="w-1.5 shrink-0 bg-brand-green" aria-hidden="true" data-id="element-626" />
        <div className="flex w-8 shrink-0 items-center justify-center text-border group-hover:text-text-secondary" data-id="element-627">
          <GripVerticalIcon size={16} aria-hidden="true" data-id="element-628" />
        </div>

        <div className="min-w-0 flex-1 py-3 pr-3 sm:pr-4" data-id="element-629">
          <div className="flex flex-wrap items-center gap-1.5" data-id="element-630">
            <h3 className="font-heading text-base" data-id="element-631">{order.id}</h3>
            <span className="rounded-md border border-brand-green-dark/40 bg-brand-yellow-light px-2 py-0.5 text-xs font-semibold text-text-primary" data-id="element-632">
              {recipe.name}
            </span>
            {order.source === 'sap' ? <SapMark bare data-id="element-633" /> : null}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-secondary" data-id="element-634">
            <span className="inline-flex items-center gap-1.5" data-id="element-635">
              <div size={14} aria-hidden="true" data-id="element-636" />
              <strong className="font-heading text-text-primary" data-id="element-637">
                {order.lots}
              </strong>{' '}
              Lots
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-638">
              <strong className="font-heading text-text-primary" data-id="element-639">
                {formatKg(totalKg)}
              </strong>{' '}
              Gesamtmenge
            </span>
            <span data-id="element-640">{formatKg(recipe.kilogramsPerLot)} / Lot</span>
          </div>
        </div>

        <div className="hidden w-[104px] shrink-0 border-l border-border p-2.5 text-right sm:flex sm:flex-col sm:items-end sm:justify-between" data-id="element-641">
          <span className="rounded-full bg-brand-yellow-light px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-primary" data-id="element-642">
            Prio {priority}
          </span>
          <div data-id="element-643">
            <p className="font-heading text-base" data-id="element-644">{formatKg(totalKg)}</p>
            <p className="text-[10px] text-text-secondary" data-id="element-645">Gesamtmenge</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border bg-surface-subtle px-3 py-1.5 sm:hidden" data-id="element-646">
        <span className="text-xs font-semibold text-text-secondary" data-id="element-647">
          Priorität {priority}
        </span>
        <span className="text-sm font-heading" data-id="element-648">{formatKg(totalKg)}</span>
      </div>
    </motion.article>;
}
function ProductionPlannerSkeleton() {
  return <div className="grid animate-pulse gap-4 xl:grid-cols-[172px_minmax(0,1fr)_172px]" role="status" aria-label="Produktionsplan wird geladen" data-id="element-649">
      <PlannerDaySkeleton data-id="element-650" />
      <main className="min-w-0" data-id="element-651">
        <div className="overflow-hidden border border-border bg-surface shadow-sm" data-id="element-652">
          <div className="flex flex-col gap-5 border-b border-border px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-653">
            <div data-id="element-654">
              <div className="h-4 w-20 rounded bg-border" data-id="element-655" />
              <div className="mt-3 h-8 w-60 max-w-full rounded bg-border sm:w-80" data-id="element-656" />
              <div className="mt-3 h-4 w-44 rounded bg-border sm:w-56" data-id="element-657" />
            </div>
            <div className="flex flex-wrap gap-2.5" data-id="element-658">
              <div className="h-8 w-20 rounded-md bg-border" data-id="element-659" />
              <div className="h-8 w-24 rounded-md bg-border" data-id="element-660" />
              <div className="h-8 w-16 rounded-md bg-border" data-id="element-661" />
              <div className="h-9 w-36 rounded-md bg-border" data-id="element-662" />
            </div>
          </div>
          <div className="h-10 border-b border-border bg-surface-subtle" data-id="element-663" />
          <div className="min-h-[470px] space-y-3 p-4 sm:p-6" data-id="element-664">
            {Array.from({
            length: 4
          }, (_, index) => <div key={index} className="overflow-hidden rounded-xl border border-border bg-surface" data-id="element-665">
                  <div className="flex min-h-[112px]" data-id="element-666">
                    <div className="w-1.5 shrink-0 bg-border" data-id="element-667" />
                    <div className="w-8 shrink-0" data-id="element-668" />
                    <div className="flex-1 py-4 pr-4" data-id="element-669">
                      <div className="h-5 w-40 rounded bg-border" data-id="element-670" />
                      <div className="mt-3 h-4 w-28 rounded bg-border" data-id="element-671" />
                      <div className="mt-3 h-3 w-56 max-w-[80%] rounded bg-border" data-id="element-672" />
                    </div>
                    <div className="hidden w-[104px] border-l border-border p-3 sm:block" data-id="element-673">
                      <div className="ml-auto h-5 w-12 rounded-full bg-border" data-id="element-674" />
                      <div className="mt-11 ml-auto h-4 w-16 rounded bg-border" data-id="element-675" />
                    </div>
                  </div>
                </div>)}
          </div>
        </div>
      </main>
      <PlannerDaySkeleton align="right" data-id="element-676" />
    </div>;
}
function PlannerDaySkeleton({
  align
}: {
  align?: 'right';
}) {
  return <aside className={`hidden min-h-[520px] border border-border bg-surface-subtle p-3 xl:block ${align === 'right' ? 'order-last' : ''}`} aria-hidden="true" data-id="element-677">
      <div className="h-3 w-20 rounded bg-border" data-id="element-678" />
      <div className="mt-3 h-6 w-24 rounded bg-border" data-id="element-679" />
      <div className="mt-2 h-3 w-20 rounded bg-border" data-id="element-680" />
      <div className="mt-5 space-y-2" data-id="element-681">
        {Array.from({
        length: 3
      }, (_, index) => <div key={index} className="rounded-lg border border-border bg-surface p-3" data-id="element-682">
              <div className="h-3 w-20 rounded bg-border" data-id="element-683" />
              <div className="mt-3 h-3 w-full rounded bg-border" data-id="element-684" />
              <div className="mt-3 h-3 w-16 rounded bg-border" data-id="element-685" />
              <div className="mt-3 h-1 w-full rounded-full bg-border" data-id="element-686" />
            </div>)}
      </div>
    </aside>;
}
function InsertionIndicator() {
  return <motion.div layout initial={{
    opacity: 0,
    scaleX: 0.92
  }} animate={{
    opacity: 1,
    scaleX: 1
  }} exit={{
    opacity: 0,
    scaleX: 0.92
  }} transition={{
    duration: 0.14,
    ease: 'easeOut'
  }} className="flex h-2 items-center px-1" aria-hidden="true" data-id="element-687">
      <span className="h-0.5 w-full rounded-full bg-brand-green shadow-[0_0_0_1px_theme(colors.surface)]" data-id="element-688" />
    </motion.div>;
}
function SapMark({
  compact = false,
  bare = false
}: {
  compact?: boolean;
  bare?: boolean;
}) {
  return <span className={`inline-flex items-center ${bare ? '' : 'rounded-sm border border-border bg-surface px-1'} ${compact ? 'h-4' : 'h-5'}`} aria-label="SAP-Auftrag" title="SAP-Auftrag" data-id="element-689">
      <img src="https://cdn.magicpatterns.com/uploads/sMZooMAHf11pkCTQ8baZc4/sap.svg" alt="SAP" className={`${compact ? 'h-3.5' : bare ? 'h-[30px]' : 'h-5'} w-auto`} data-id="element-690" />
    </span>;
}
function Statistic({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <span className="rounded-md border border-border bg-surface-subtle px-2.5 py-1.5 text-xs text-text-secondary" data-id="element-691">
      <strong className="font-heading text-text-primary" data-id="element-692">{value}</strong>{' '}
      {label}
    </span>;
}
function SummaryMetric({
  label,
  value,
  isEmphasized = false
}: {
  label: string;
  value: string;
  isEmphasized?: boolean;
}) {
  return <div data-id="element-693">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-text-secondary" data-id="element-694">
        {label}
      </p>
      <p className={`mt-1 font-heading text-xl ${isEmphasized ? 'text-text-primary' : ''}`} data-id="element-695">
        {value}
      </p>
    </div>;
}
function getDay(key: DayKey) {
  return weekDays.find(day => day.key === key) ?? weekDays[0];
}
function getRecipe(recipeId: string) {
  return recipes.find(recipe => recipe.id === recipeId) ?? recipes[0];
}
function getTotals(orders: PlannedOrder[]) {
  return orders.reduce((totals, order) => {
    const recipe = getRecipe(order.recipeId);
    return {
      lots: totals.lots + order.lots,
      kilograms: totals.kilograms + recipe.kilogramsPerLot * order.lots
    };
  }, {
    lots: 0,
    kilograms: 0
  });
}
function formatKg(value: number) {
  return `${value.toLocaleString('de-DE')} kg`;
}